/**
 * Drag-zoom: a view on the data already drawn, on every axis.
 *
 * A time-axis zoom used to push its window into the date pickers and re-query,
 * so the server re-aggregated at the zoomed resolution. The cost was that zoom
 * was not a zoom: the narrowed data became the whole chart, so there was
 * nothing to reset to and the query had quietly changed under you. Now a zoom
 * only narrows the view - the readout and **Reset zoom** say so - and a
 * narrower query is what the date pickers are for. The precision-zoom bars
 * (`render/precision-zoom.ts`) are the same kind of view change.
 *
 * In a grid the panels share one X, so a zoom in one is applied to the rest.
 *
 * The `inRebuild` guard skips the setScale hook fired synchronously during
 * `new uPlot()`.
 */

import type uPlot from 'uplot';
import { runtime, type XExtent } from './plot-runtime.ts';
import { plotXWindow } from './plot-ui-state.ts';
import { fmtTimeShort, formatNum } from './render/format.ts';
import {
  formatPeriodDuration,
  periodComparison,
  periodDurationSeconds,
  type PlotPanelSpec,
} from '../../state/period-comparison.ts';
import { refinePeriodComparison, resetPeriodComparisonView } from './plot-actions.ts';

export function updateXWindowReadout(
  min: number | null,
  max: number | null,
  isTime: boolean,
  isZoomed: boolean,
): void {
  if (min == null || max == null || !Number.isFinite(min) || !Number.isFinite(max)) {
    plotXWindow.set(null);
    return;
  }
  const a = isTime ? fmtTimeShort(min) : formatNum(min);
  const b = isTime ? fmtTimeShort(max) : formatNum(max);
  // A live chart moves its window every frame; the readout only changes when
  // its text does, so the header is not re-rendered sixty times a second.
  const cur = plotXWindow.value;
  if (cur && cur.a === a && cur.b === b && cur.isZoomed === isZoomed) return;
  plotXWindow.set({ a, b, isZoomed });
}

/** True when [min,max] equals the drawn data range within 0.1% (no-op zoom). */
export function scaleMatches(min: number, max: number, range: XExtent): boolean {
  const span = Math.max(1e-9, range.max - range.min);
  const eps = span * 0.001;
  return Math.abs(min - range.min) < eps && Math.abs(max - range.max) < eps;
}

/** Set while one grid panel's zoom is copied to the others. */
let propagating = false;

export function onXScaleChanged(u: uPlot, isTime: boolean): void {
  if (runtime.inRebuild || propagating) return;
  if (!runtime.dataRange) return;
  const sc = u.scales.x;
  const min = sc?.min;
  const max = sc?.max;
  if (min == null || max == null || !Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return;
  }
  const zoomed = !scaleMatches(min, max, runtime.dataRange);
  if (zoomed) updateXWindowReadout(min, max, isTime, true);
  else updateXWindowReadout(runtime.dataRange.min, runtime.dataRange.max, isTime, false);

  // Grid panels share one X: carry the zoom to the others. Skipping panels
  // already on this window is what stops the copies echoing back - uPlot runs
  // their hooks after this returns, when `propagating` is already false.
  if (runtime.gridInstances.length > 1) {
    propagating = true;
    try {
      for (const other of runtime.gridInstances) {
        const sc = other.scales.x;
        if (other === u || (sc?.min === min && sc?.max === max)) continue;
        other.setScale('x', { min, max });
      }
    } finally {
      propagating = false;
    }
  }
}

/** Elapsed comparison zoom never mutates the global A date range. */
export function onPeriodXScaleChanged(
  u: uPlot,
  _panel: PlotPanelSpec,
  elapsed: boolean,
  temporal: boolean,
): void {
  const comparison = periodComparison.value;
  if (runtime.inRebuild || !comparison || !comparison.linkNavigation || !elapsed || !temporal) return;
  const min = u.scales.x?.min;
  const max = u.scales.x?.max;
  if (min == null || max == null || !Number.isFinite(min) || !Number.isFinite(max) || !(max > min)) return;
  const longest = Math.max(
    ...(runtime.periodBaseResults ?? []).map((result) => periodDurationSeconds(result.panel) ?? 0),
  );
  if (longest > 0 && scaleMatches(min, max, { min: 0, max: longest })) {
    plotXWindow.set(null);
    return;
  }
  const current = runtime.periodViewWindow;
  if (current && scaleMatches(min, max, current)) return;
  plotXWindow.set({
    a: `+${formatPeriodDuration(Math.max(1, Math.round(min)))}`,
    b: `+${formatPeriodDuration(Math.max(1, Math.round(max)))}`,
    isZoomed: true,
  });
  if (runtime.zoomTimer) clearTimeout(runtime.zoomTimer);
  runtime.zoomTimer = setTimeout(() => {
    runtime.zoomTimer = null;
    void refinePeriodComparison(min, max);
  }, 350);
}

/**
 * Widen the x scale back over the drawn data - exactly what double-clicking the
 * chart does, as a button in the chart header.
 *
 * View-only, deliberately. A time-axis drag also narrows the date range and
 * refetches, so the data on screen *is* the zoomed window; this puts the whole
 * of it back in view rather than re-running the wider query, which is what the
 * date pickers are for. Every grid panel is reset, since they share one x.
 */
export function resetZoom(): void {
  if (resetPeriodComparisonView()) return;
  const targets = runtime.gridInstances.length
    ? runtime.gridInstances
    : runtime.instance
      ? [runtime.instance]
      : [];
  for (const u of targets) {
    const xs = u.data[0] as number[] | undefined;
    const min = runtime.dataRange?.min ?? xs?.[0];
    const max = runtime.dataRange?.max ?? (xs ? xs[xs.length - 1] : undefined);
    if (min == null || max == null || !(max > min)) continue;
    u.setScale('x', { min, max });
  }
}


